/**
 * Seg — кнопка сегмент-переключателя (news.tsx View toggle, вкладки в
 * processes.tsx). Сведены два инлайн-варианта: с иконкой (news.tsx) и с
 * badge/accent (processes.tsx) — там же остаётся, мигрирует следующая волна.
 *
 * accent — акцентный CTA-сегмент ("+ Новый процесс"): не участвует в
 * active/badge, всегда залит $primary с плюс-иконкой.
 *
 * badge — счётчик (0 тоже рисуется: у вкладок «Процессов» счётчик всегда виден);
 * badgeTone: primary — по умолчанию, danger — срочное («Ждут моего решения»), muted — справочный.
 */
import { Stack, Text, XStack, type GetProps } from 'tamagui'

import { tone } from '../theme/tones'

import { palette } from '../theme/palette'
import { Icon } from './Icon'
import { pressable } from './primitives'

export function Seg({
  active,
  label,
  icon,
  badge,
  badgeTone = 'primary',
  accent,
  onPress,
  ...rest
}: {
  active: boolean
  label: string
  icon?: string
  badge?: number
  badgeTone?: 'primary' | 'danger' | 'muted'
  accent?: boolean
  onPress: () => void
} & Omit<GetProps<typeof XStack>, 'onPress'>) {
  if (accent) {
    return (
      <XStack
        {...pressable}
        onPress={onPress}
        cursor="pointer"
        animation="quick"
        alignItems="center"
        gap={6}
        paddingHorizontal={14}
        paddingVertical={7}
        borderRadius={5}
        backgroundColor="$primary"
        hoverStyle={{ opacity: 0.9 }}
        pressStyle={{ opacity: 0.85 }}
      >
        <Icon name="plus" size={14} color="$onPrimary" />
        <Text fontSize={13} fontWeight="700" color="$onPrimary">
          {label}
        </Text>
      </XStack>
    )
  }
  return (
    <XStack
      {...pressable}
      onPress={onPress}
      cursor="pointer"
      animation="quick"
      alignItems="center"
      gap={6}
      paddingHorizontal={12}
      paddingVertical={7}
      borderRadius={5}
      backgroundColor={active ? '$background' : 'transparent'}
      hoverStyle={active ? {} : { backgroundColor: '$backgroundHover' }}
      pressStyle={{ scale: 0.97 }}
      {...(active
        ? { shadowColor: palette.shadow, shadowOpacity: 0.12, shadowRadius: 6, shadowOffset: { width: 0, height: 1 } }
        : null)}
      role="tab"
      aria-selected={active}
      {...rest}
    >
      {icon && <Icon name={icon} size={15} color={active ? '$primary' : '$tertiary'} />}
      <Text fontSize={13} fontWeight="600" color={active ? '$color' : '$muted'} numberOfLines={1}>
        {label}
      </Text>
      {badge != null ? (
        <Stack
          minWidth={18}
          height={16}
          borderRadius={999}
          backgroundColor={badgeTone === 'danger' ? tone('red').solid : badgeTone === 'muted' ? '$borderSecondary' : '$primary'}
          alignItems="center"
          justifyContent="center"
          paddingHorizontal={6}
        >
          <Text fontSize={11} fontWeight="700" color={badgeTone === 'muted' ? '$muted' : '$onPrimary'}>
            {badge}
          </Text>
        </Stack>
      ) : null}
    </XStack>
  )
}
